/**
 * RackSavant design tokens.
 *
 * Platform-agnostic on purpose: web (CSS vars), React Native (StyleSheet) and
 * Electron all read from this one file, so the product feels like one object
 * no matter which screen it is on.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE VISUAL THESIS: a fashion magazine, not an app.
 *
 * The first pass of this product looked like every other dark-mode SaaS —
 * a neon accent on a violet-black field, an 8/12/16/24 corner-radius ladder,
 * emoji in pills, vector clip-art. Competent, generic, and completely wrong for
 * an audience whose entire job is having taste.
 *
 * So the rules here are print rules:
 *
 *   1. TYPE IS THE DESIGN. A magazine has no illustrations holding it together;
 *      it has a masthead, a headline set enormous, and a caption. Display serif
 *      with real stroke contrast, set tight and slightly too big.
 *   2. CORNERS ARE SQUARE. Printed images are rectangles. `radius.sm` is 0 on
 *      purpose. The only curves in the product are die-cut photo stickers and
 *      genuinely circular crops.
 *   3. RULES ARE HAIRLINES. 1px, high contrast. No 1px-of-12%-white "borders"
 *      that read as mush.
 *   4. COLOUR IS RESTRAINED, THEN VIOLENT. Ink, bone, oxblood — and one
 *      registration-mark flash used like a highlighter, never as a fill.
 *   5. SURFACES HAVE TOOTH. Grain, halftone, misregistration. Nothing is a
 *      flat #RRGGBB field; there is always paper under it.
 *   6. MOTION SETS, IT DOESN'T BOUNCE. Type reveals like it is being printed.
 *      Marquees scroll. Images wipe. Nothing springs, nothing giggles.
 *
 * Mood colours (packages/core/src/moods.ts) are deliberately NOT part of this
 * palette — they are content, chosen by the user, and they are allowed to be
 * loud against this restraint.
 * ─────────────────────────────────────────────────────────────────────────────
 */

export const color = {
  /**
   * Ground — a true neutral near-black. The old violet undertone is what made
   * this read as "dark mode" instead of as ink.
   */
  ink: '#0B0B0C',
  ink2: '#131313',
  ink3: '#1C1B1A',
  ink4: '#2A2825',

  /** Bone, not white. Newsprint and uncoated stock, warm and slightly dirty. */
  paper: '#EFEBE3',
  paperDim: '#BDB7AC',
  muted: '#8A857B',
  faint: '#575249',

  /**
   * Oxblood. The accent is a FASHION colour, not a UI colour — a bruised,
   * expensive red that looks like it was mixed rather than picked.
   */
  accent: '#7E2436',
  accentSoft: '#A8404F',
  accentDeep: '#4A121F',

  /**
   * The registration flash. Print-shop orange-red, used the way a highlighter
   * or a crop mark is: for one live thing on the page. Never a fill, never a
   * whole button, never a gradient.
   */
  flash: '#FF3B00',

  gold: '#F5C518',
  mint: '#4ADE80',
  cyan: '#22D3EE',
  danger: '#FF4D6D',

  line: 'rgba(255,255,255,0.09)',
  lineStrong: 'rgba(255,255,255,0.18)',
  scrim: 'rgba(6,6,10,0.72)',
  glassFill: 'rgba(255,255,255,0.05)',
} as const;

/** Light theme, used only on the public marketing site's alternate sections. */
export const lightColor = {
  ink: '#FBFAF8',
  ink2: '#F2F0EC',
  ink3: '#E7E4DE',
  ink4: '#D8D4CC',
  paper: '#0F0F14',
  paperDim: '#3A3A44',
  muted: '#6B6B78',
  faint: '#9A9AA6',
  accent: '#7E2436',
  accentSoft: '#A8404F',
  accentDeep: '#4A121F',
  gold: '#B8940F',
  mint: '#1FA355',
  cyan: '#0E90A8',
  danger: '#D62B4A',
  line: 'rgba(15,15,20,0.10)',
  lineStrong: 'rgba(15,15,20,0.20)',
  scrim: 'rgba(255,255,255,0.75)',
  glassFill: 'rgba(15,15,20,0.04)',
} as const;

/**
 * The `brand` / `savant` / `gold` gradients are gone with the neon they were
 * made of. Nothing in the product is a gradient any more except the scrim that
 * makes type legible over a photograph, which is a printing problem, not decoration.
 */
export const gradient = {
  /** Applied over photos so text stays readable at the bottom of a card. */
  scrimUp: ['rgba(10,10,14,0)', 'rgba(10,10,14,0.92)'] as [string, string],
} as const;

export const font = {
  /** Editorial serif — headlines, the verdict line, the marketing hero. */
  display: '"Instrument Serif", "Playfair Display", Georgia, "Times New Roman", serif',
  /** Everything functional. */
  sans: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, system-ui, sans-serif',
  mono: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
} as const;

/** React Native cannot use font stacks — it needs one loaded family name. */
export const nativeFont = {
  display: 'InstrumentSerif',
  displayItalic: 'InstrumentSerif-Italic',
  sans: 'Inter',
  sansMedium: 'Inter-Medium',
  sansSemi: 'Inter-SemiBold',
  sansBold: 'Inter-Bold',
} as const;

/**
 * A magazine type scale: a violent jump from headline to caption with almost
 * nothing in between. Display sizes are set with NEGATIVE leading (lineHeight
 * below size) so multi-line headlines lock together as a block of texture, and
 * tracked tight — the opposite of the airy, evenly-stepped web default.
 */
export const type = {
  /** Cover line. Set it far bigger than feels sensible, then go bigger. */
  masthead: { size: 132, lineHeight: 112, weight: '400', letterSpacing: -6 },
  hero: { size: 84, lineHeight: 76, weight: '400', letterSpacing: -3.2 },
  display: { size: 52, lineHeight: 50, weight: '400', letterSpacing: -1.8 },
  title: { size: 30, lineHeight: 32, weight: '400', letterSpacing: -0.8 },
  heading: { size: 19, lineHeight: 24, weight: '500', letterSpacing: -0.2 },
  body: { size: 16, lineHeight: 25, weight: '400', letterSpacing: 0 },
  bodySm: { size: 14, lineHeight: 21, weight: '400', letterSpacing: 0 },
  /** Photo credit / byline. Small, tracked out, usually italic. */
  caption: { size: 12, lineHeight: 16, weight: '400', letterSpacing: 0.2 },
  label: { size: 12, lineHeight: 15, weight: '500', letterSpacing: 0.4 },
  /** Crop-mark micro type. ALWAYS uppercase, always heavily tracked. */
  eyebrow: { size: 10, lineHeight: 12, weight: '600', letterSpacing: 2.4 },
} as const;

export const space = {
  '0': 0,
  '1': 4,
  '2': 8,
  '3': 12,
  '4': 16,
  '5': 20,
  '6': 24,
  '7': 32,
  '8': 40,
  '9': 56,
  '10': 72,
  '11': 96,
  '12': 128,
} as const;

/**
 * Corners are square. A printed image is a rectangle; softening every edge by
 * 12px is the single fastest way to make an interface look like a template.
 *
 * The old 8/12/18/26/34 ladder is gone. What remains: zero, a 2px optical
 * softening for things you physically touch, and `pill` reserved for objects
 * that are genuinely lozenge-shaped — a live-status dot, a count badge. Nothing
 * else gets a radius. Photo stickers carry their own die-cut silhouette in the
 * PNG and must never sit inside a rounded box.
 */
export const radius = {
  sm: 0,
  md: 0,
  lg: 0,
  xl: 0,
  '2xl': 0,
  /** Inputs and buttons only — enough to not look laser-cut, not enough to see. */
  touch: 2,
  pill: 999,
  /** Look cards are prints. Prints have corners. */
  card: 0,
} as const;

/**
 * Depth only, never colour. The two neon `glow` entries went with the accent
 * they belonged to — nothing in this product emits light.
 */
export const shadow = {
  sm: '0 1px 2px rgba(0,0,0,0.30)',
  md: '0 6px 20px rgba(0,0,0,0.38)',
  lg: '0 18px 50px rgba(0,0,0,0.48)',
} as const;

/**
 * Motion sets; it does not bounce.
 *
 * The reference is a printing press and a film scanner, not a phone toggle:
 * type wipes in line by line, images resolve from grain, marquees run at a
 * constant speed forever. `ease` is a near-linear editorial curve — the old
 * overshooting spring made everything feel like a toy.
 */
export const motion = {
  instant: 90,
  fast: 180,
  base: 320,
  slow: 560,
  /** The reveal when a look finishes developing. Deliberately unhurried. */
  reveal: 1200,
  /** One full pass of a marquee. Slow enough to read, never restarting visibly. */
  marquee: 28000,
  /** Ambient drift of the chaos field. Should never look like it is animating. */
  drift: 42000,
  /** Editorial: fast out of the gate, long settle. No overshoot, ever. */
  ease: 'cubic-bezier(0.16, 0.84, 0.24, 1)',
  easeIn: 'cubic-bezier(0.7, 0, 0.84, 0)',
  /** Mechanical, for anything that should feel like a machine part moving. */
  easeLinear: 'cubic-bezier(0.4, 0, 0.6, 1)',
  spring: { damping: 26, stiffness: 210, mass: 1 },
} as const;

export const layout = {
  maxWidth: 1240,
  maxWidthNarrow: 760,
  headerHeight: 64,
  tabBarHeight: 72,
  /** Look images are always shot 2:3 — the whole grid depends on it. */
  lookAspect: 2 / 3,
  garmentAspect: 3 / 4,
} as const;

export const zIndex = {
  base: 0,
  sticky: 10,
  header: 20,
  drawer: 30,
  modal: 40,
  toast: 50,
} as const;

export const breakpoint = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
} as const;

export const tokens = {
  color,
  lightColor,
  gradient,
  font,
  nativeFont,
  type,
  space,
  radius,
  shadow,
  motion,
  layout,
  zIndex,
  breakpoint,
} as const;

export type Tokens = typeof tokens;

/** Emit the dark-theme tokens as CSS custom properties. */
export function cssVariables(theme: 'dark' | 'light' = 'dark'): string {
  const palette = theme === 'dark' ? color : lightColor;
  const lines: string[] = [];
  for (const [key, value] of Object.entries(palette)) {
    lines.push(`  --rs-color-${kebab(key)}: ${value};`);
  }
  for (const [key, value] of Object.entries(space)) {
    lines.push(`  --rs-space-${key}: ${value}px;`);
  }
  for (const [key, value] of Object.entries(radius)) {
    lines.push(`  --rs-radius-${kebab(key)}: ${typeof value === 'number' ? `${value}px` : value};`);
  }
  for (const [key, value] of Object.entries(shadow)) {
    lines.push(`  --rs-shadow-${kebab(key)}: ${value};`);
  }
  lines.push(`  --rs-font-display: ${font.display};`);
  lines.push(`  --rs-font-sans: ${font.sans};`);
  lines.push(`  --rs-font-mono: ${font.mono};`);
  lines.push(`  --rs-ease: ${motion.ease};`);
  return lines.join('\n');
}

function kebab(value: string): string {
  return value.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
}

/** rgba() from a hex + alpha, used for glows keyed off a mood colour. */
export function alpha(hex: string, a: number): string {
  const clean = hex.replace('#', '');
  const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}
